import React, { useEffect, useState } from "react";
import { Flex, Heading, Notification, Text } from "@telia/teddy";
import { API_URL_RETAILER_NEWS } from "../../api";
import "./Nyheter.less";

type NewsType = "INFO" | "WARNING" | "ERROR";

type NewsItem = {
    id: string;
    heading: string;
    ingress: string;
    link: string | null;
    newsType: NewsType;
    updatedAt: string;
};

const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("nb-NO", { day: "2-digit", month: "2-digit", year: "numeric" });

const Nyheter: React.FC = () => {
    const [news, setNews] = useState<NewsItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setHasError(false);

        fetch(API_URL_RETAILER_NEWS)
            .then((res) => {
                if (!res.ok) throw new Error(`Serverfeil: ${res.status}`);
                return res.json() as Promise<NewsItem[]>;
            })
            .then((data) => {
                if (cancelled) return;
                setNews(data);
            })
            .catch(() => {
                if (cancelled) return;
                setHasError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div className="retailer-dashboard-wrapper">
            <Heading as="h1" variant="title-300" mb="400">
                Nyheter
            </Heading>

            {loading && <Text>Laster...</Text>}

            {!loading && hasError && (
                <Notification type="tile" variant="error">
                    <Notification.Icon />
                    <Notification.Text>Kunne ikke hente nyheter. Prøv igjen senere.</Notification.Text>
                </Notification>
            )}

            {!loading && !hasError && news.length === 0 && <Text>Ingen nyheter foreløpig.</Text>}

            {!loading && !hasError && (
                <Flex direction="column" gap="300">
                    {news.map((item) => (
                        <div key={item.id} className={`nyheter-card nyheter-card--${item.newsType.toLowerCase()}`}>
                            <Flex justify="between" align="center" mb="100">
                                <Heading mb="100" as="h2" variant="title-100">
                                    {item.heading}
                                </Heading>
                                <Text variant="additional-100">{formatDate(item.updatedAt)}</Text>
                            </Flex>
                            <Text style={{ whiteSpace: "pre-wrap" }}>{item.ingress}</Text>
                            {item.link && (
                                <Text mt="100">
                                    <a href={item.link} target="_blank" rel="noopener noreferrer">
                                        Les mer
                                    </a>
                                </Text>
                            )}
                        </div>
                    ))}
                </Flex>
            )}
        </div>
    );
};

export default Nyheter;
